/**
 * Analytics emitter (POL-589).
 *
 * Fire-and-forget reporter for widget lifecycle events. The emitter
 * batches events into a single POST per microtask tick and prefers
 * `navigator.sendBeacon` over `fetch(..., { keepalive: true })` so
 * that events survive page unload without blocking navigation.
 *
 * Contract:
 *   - `embed.viewed` fires once per successful widget mount. Duplicate
 *     views for the same `polstId | campaignId` within a configurable
 *     window (default 5 s) are silently dropped — guards against
 *     HMR / StrictMode / double-hydration.
 *   - `embed.voted` fires after every successful vote (polst card or
 *     campaign step). NOT deduped.
 *   - `embed.completed` fires once when a campaign reaches its summary
 *     screen.
 *   - Do-Not-Track wins. When `navigator.doNotTrack === "1"` the
 *     emitter turns into a no-op at construction.
 *   - All transport failures are swallowed. The emitter never throws
 *     into the host page.
 *
 * The module is SSR-safe: `getSharedEmitter()` is a lazy getter, so
 * merely importing the file in Node has zero side effects. The first
 * call in a browser constructs the singleton; subsequent calls return
 * the same instance.
 *
 * Tests inject every collaborator (fetch, sendBeacon, clock, window),
 * so the module can be exercised end-to-end in jsdom.
 */

// ─── Public types ───────────────────────────────────────────────────────────

export type EmbedEventType = "embed.viewed" | "embed.voted" | "embed.completed";

export interface EmbedEventContext {
  referrer?: string;
  utm?: Record<string, string>;
  ua?: string;
  viewport?: { w: number; h: number };
}

export interface EmbedEvent {
  type: EmbedEventType;
  occurredAt: string;
  polstId?: string;
  campaignId?: string;
  brandId?: string;
  context: EmbedEventContext;
}

export interface EmbedEventInput {
  type: EmbedEventType;
  polstId?: string;
  campaignId?: string;
  brandId?: string;
  /** Override context collection (tests). */
  context?: EmbedEventContext;
  /** Override `occurredAt` (tests). */
  occurredAt?: string;
}

/**
 * Minimal subset of `Window` the emitter touches. Kept narrow so tests
 * can fake it without recreating a jsdom environment.
 */
export interface AnalyticsWindow {
  document?: { referrer?: string } | undefined;
  location?: { search?: string; origin?: string } | undefined;
  navigator?: {
    userAgent?: string;
    doNotTrack?: string | null;
    sendBeacon?: (url: string, data?: Blob | string | FormData) => boolean;
  };
  innerWidth?: number;
  innerHeight?: number;
}

export interface AnalyticsEmitterOptions {
  /** Absolute URL for the events endpoint. Required. */
  url: string;
  /** Fetch override (defaults to `globalThis.fetch`). */
  fetch?: typeof fetch;
  /** sendBeacon override (defaults to `navigator.sendBeacon`). */
  sendBeacon?: (url: string, data?: Blob | string | FormData) => boolean;
  /** Clock override (defaults to `Date.now`). */
  now?: () => number;
  /** Window override used for DNT + context collection. */
  window?: AnalyticsWindow | null;
  /** Microtask scheduler — tests substitute `setImmediate`-style. */
  schedule?: (cb: () => void) => void;
  /** Dedupe window for `embed.viewed`. Default 5000 ms. */
  dedupeWindowMs?: number;
  /**
   * Max batch size per drain. Default 50 (matches server-side cap).
   * Events above the cap are split across multiple drains.
   */
  maxBatchSize?: number;
  /**
   * Override the DNT signal. When set, bypasses `navigator.doNotTrack`.
   * Only used by tests.
   */
  doNotTrackOverride?: boolean;
}

export interface AnalyticsEmitter {
  emit(event: EmbedEventInput): void;
  flush(): void;
  /** True when DNT killed the emitter at construction. */
  readonly isNoop: boolean;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_DEDUPE_WINDOW_MS = 5000;
const DEFAULT_BATCH_SIZE = 50;
const DEDUPE_PURGE_AFTER_MS = 60_000;

// ─── Helpers ────────────────────────────────────────────────────────────────

function resolveWindow(explicit: AnalyticsWindow | null | undefined): AnalyticsWindow | null {
  if (explicit === null) return null;
  if (explicit) return explicit;
  if (typeof window === "undefined") return null;
  return window as unknown as AnalyticsWindow;
}

function detectDoNotTrack(win: AnalyticsWindow | null, override?: boolean): boolean {
  if (typeof override === "boolean") return override;
  if (!win || !win.navigator) return false;
  // `navigator.doNotTrack` is normalized to the string "1" by all
  // major browsers when the header is set. "yes" is a legacy Gecko
  // spelling; include it for safety.
  const dnt = win.navigator.doNotTrack;
  return dnt === "1" || dnt === "yes";
}

function defaultSchedule(cb: () => void): void {
  if (typeof queueMicrotask === "function") {
    queueMicrotask(cb);
    return;
  }
  Promise.resolve().then(cb);
}

function parseUtm(search: string | undefined): Record<string, string> | undefined {
  if (!search) return undefined;
  const trimmed = search.startsWith("?") ? search.slice(1) : search;
  if (!trimmed) return undefined;
  const result: Record<string, string> = {};
  for (const part of trimmed.split("&")) {
    if (!part) continue;
    const eq = part.indexOf("=");
    const rawKey = eq === -1 ? part : part.slice(0, eq);
    const rawVal = eq === -1 ? "" : part.slice(eq + 1);
    let key: string;
    let val: string;
    try {
      key = decodeURIComponent(rawKey);
      val = decodeURIComponent(rawVal);
    } catch {
      continue;
    }
    if (!key.toLowerCase().startsWith("utm_")) continue;
    result[key] = val;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function collectContext(win: AnalyticsWindow | null): EmbedEventContext {
  if (!win) return {};
  const ctx: EmbedEventContext = {};
  const referrer = win.document?.referrer;
  if (referrer && referrer.length > 0) ctx.referrer = referrer;
  const utm = parseUtm(win.location?.search);
  if (utm) ctx.utm = utm;
  const ua = win.navigator?.userAgent;
  if (ua) ctx.ua = ua;
  if (typeof win.innerWidth === "number" && typeof win.innerHeight === "number") {
    ctx.viewport = { w: win.innerWidth, h: win.innerHeight };
  }
  return ctx;
}

function dedupeKey(event: EmbedEvent): string {
  const id = event.polstId ?? event.campaignId ?? "_";
  return `${event.type}:${id}`;
}

// ─── Emitter ────────────────────────────────────────────────────────────────

/**
 * Create an analytics emitter. See {@link AnalyticsEmitterOptions}.
 *
 * Callers own the lifetime. For the widget's shared instance see
 * {@link getSharedEmitter}.
 */
export function createAnalyticsEmitter(options: AnalyticsEmitterOptions): AnalyticsEmitter {
  const win = resolveWindow(options.window);
  const dnt = detectDoNotTrack(win, options.doNotTrackOverride);

  if (dnt) {
    return {
      emit(): void {
        /* DNT no-op */
      },
      flush(): void {
        /* DNT no-op */
      },
      isNoop: true,
    };
  }

  const url = options.url;
  const dedupeWindowMs = options.dedupeWindowMs ?? DEFAULT_DEDUPE_WINDOW_MS;
  const maxBatchSize = options.maxBatchSize ?? DEFAULT_BATCH_SIZE;
  const now = options.now ?? (() => Date.now());
  const schedule = options.schedule ?? defaultSchedule;

  const resolveFetch = (): typeof fetch | null => {
    if (options.fetch) return options.fetch;
    const g = globalThis as unknown as { fetch?: typeof fetch };
    if (typeof g.fetch === "function") return g.fetch.bind(globalThis);
    return null;
  };
  const resolveBeacon = ():
    | ((url: string, data?: Blob | string | FormData) => boolean)
    | null => {
    if (options.sendBeacon) return options.sendBeacon;
    const nav = win?.navigator;
    if (nav && typeof nav.sendBeacon === "function") {
      return nav.sendBeacon.bind(nav);
    }
    return null;
  };

  const queue: EmbedEvent[] = [];
  const lastSeen = new Map<string, number>();
  let draining = false;

  const scheduleDrain = (): void => {
    if (draining) return;
    draining = true;
    schedule(() => {
      try {
        drainQueue();
      } finally {
        draining = false;
      }
    });
  };

  const shipBatch = (batch: EmbedEvent[]): void => {
    if (batch.length === 0) return;
    const payload = JSON.stringify({ events: batch });

    const beacon = resolveBeacon();
    if (beacon) {
      try {
        // Some sandboxes lack the Blob constructor (older jsdom/node).
        // Fall back to the plain-string body when that happens.
        let body: Blob | string = payload;
        if (typeof Blob !== "undefined") {
          try {
            body = new Blob([payload], { type: "application/json" });
          } catch {
            body = payload;
          }
        }
        if (beacon(url, body) === true) return;
      } catch {
        /* fall through to fetch */
      }
    }

    const fetchImpl = resolveFetch();
    if (!fetchImpl) return;
    try {
      const result = fetchImpl(url, {
        method: "POST",
        body: payload,
        keepalive: true,
        headers: { "Content-Type": "application/json" },
      });
      // Fire-and-forget: swallow rejections so hosts don't see
      // unhandled-promise warnings.
      if (result && typeof (result as Promise<unknown>).then === "function") {
        (result as Promise<unknown>).then(
          () => undefined,
          () => undefined,
        );
      }
    } catch {
      /* transport is best-effort */
    }
  };

  const drainQueue = (): void => {
    purgeDedupe();
    while (queue.length > 0) {
      const batch = queue.splice(0, maxBatchSize);
      shipBatch(batch);
    }
  };

  const purgeDedupe = (): void => {
    const cutoff = now() - DEDUPE_PURGE_AFTER_MS;
    for (const [key, ts] of lastSeen) {
      if (ts < cutoff) lastSeen.delete(key);
    }
  };

  const buildEvent = (input: EmbedEventInput): EmbedEvent => {
    const occurredAt = input.occurredAt ?? new Date(now()).toISOString();
    const context = input.context ?? collectContext(win);
    const event: EmbedEvent = {
      type: input.type,
      occurredAt,
      context,
    };
    if (input.polstId) event.polstId = input.polstId;
    if (input.campaignId) event.campaignId = input.campaignId;
    if (input.brandId) event.brandId = input.brandId;
    return event;
  };

  return {
    emit(input: EmbedEventInput): void {
      try {
        const event = buildEvent(input);
        if (event.type === "embed.viewed") {
          const key = dedupeKey(event);
          const previous = lastSeen.get(key);
          const ts = now();
          if (previous !== undefined && ts - previous < dedupeWindowMs) {
            return;
          }
          lastSeen.set(key, ts);
        }
        queue.push(event);
        scheduleDrain();
      } catch {
        /* never throw into the host */
      }
    },
    flush(): void {
      try {
        drainQueue();
      } catch {
        /* swallow */
      }
    },
    isNoop: false,
  };
}

// ─── Shared singleton ───────────────────────────────────────────────────────

import { DEFAULT_API_ORIGIN, getApiOrigin } from "./config";

const EVENTS_PATH = "/api/rest/v1/events";

/**
 * Build-time default analytics endpoint. Resolves against the build-time
 * API origin — runtime overrides via `configureOrigins()` are picked up
 * lazily inside `getSharedEmitter()` so a late `configure()` call still
 * routes events to the right host.
 */
export const DEFAULT_ANALYTICS_URL = `${DEFAULT_API_ORIGIN}${EVENTS_PATH}`;

let sharedEmitter: AnalyticsEmitter | null = null;
let sharedEndpointOverride: string | null = null;

function resolveSharedEndpoint(): string {
  return sharedEndpointOverride ?? `${getApiOrigin()}${EVENTS_PATH}`;
}

/**
 * Override the analytics endpoint for the shared emitter. Only the
 * widget's own bootstrapping code should call this; hosts that need a
 * custom endpoint should build their own emitter with
 * {@link createAnalyticsEmitter}.
 */
export function configureSharedAnalyticsUrl(url: string): void {
  sharedEndpointOverride = url;
  // Invalidate the cached singleton so the next caller rebuilds with
  // the fresh URL.
  sharedEmitter = null;
}

/**
 * Lazy getter for the widget-wide emitter. SSR-safe: when `window` is
 * absent we return a silent no-op emitter.
 */
export function getSharedEmitter(): AnalyticsEmitter {
  if (sharedEmitter) return sharedEmitter;
  if (typeof window === "undefined") {
    sharedEmitter = {
      emit(): void {
        /* SSR no-op */
      },
      flush(): void {
        /* SSR no-op */
      },
      isNoop: true,
    };
    return sharedEmitter;
  }
  sharedEmitter = createAnalyticsEmitter({ url: resolveSharedEndpoint() });
  return sharedEmitter;
}

/**
 * Reset the shared emitter. Test-only — the `__` prefix keeps it off
 * the public widget surface.
 */
export function __resetSharedEmitter(): void {
  sharedEmitter = null;
  sharedEndpointOverride = null;
}
